import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { EmptyState } from '../../Components/Common/ListStates';
import { usePermission } from '../../contexts/PermissionContext';
import {
  ActivityFeed,
  AlertPanel,
  DashboardError,
  DashboardHeader,
  KpiCard,
  KpiSkeleton,
  LimitedNotice,
  QuickActions,
} from './DashboardParts';
import useDashboardData from './useDashboardData';
import {
  deadlineInfo,
  formatDateTime,
  loadLegacyMitraOverview,
  percentOf,
} from './dashboardUtils';
import StatusBadge from '../../Components/Common/StatusBadge';

const QUICK_ACTIONS = [
  {
    label: 'Invitations',
    icon: 'ri-mail-send-line',
    to: '/apps-invitation/view',
  },
  { label: 'My offers', icon: 'ri-file-paper-2-line', to: '/apps-offer/view' },
];

const UpcomingDeadlines = ({ items }) => {
  const { can, isMenuLoading } = usePermission();

  return (
    <Card className='h-100 mb-0'>
      <CardHeader className='bg-transparent'>
        <h5 className='card-title mb-0'>Upcoming deadlines</h5>
        <p className='text-muted fs-13 mb-0'>
          Closest first. Respond before the deadline passes.
        </p>
      </CardHeader>
      <CardBody className='p-0'>
        {items.length === 0 ? (
          <EmptyState
            compact
            icon='ri-calendar-check-line'
            title='No open deadlines'
            emptyHint='Invitations and offers that still need your action will be listed here.'
          />
        ) : (
          <ul className='list-unstyled mb-0'>
            {items.map((item, i) => {
              const info = deadlineInfo(item.deadline);
              const allowed = !isMenuLoading && can('canRead', item.link);
              return (
                <li
                  key={`${item.procurementNumber}-${i}`}
                  className={`d-flex align-items-center gap-3 px-3 py-3 ${i > 0 ? 'border-top' : ''}`}
                >
                  <div className='flex-grow-1 min-width-0'>
                    <p className='mb-0 fw-medium text-truncate'>
                      {item.procurementNumber || 'Untitled'}
                    </p>
                    <p className='mb-0 text-muted fs-12 text-truncate'>
                      {item.title}
                    </p>
                    <p className='mb-0 text-muted fs-12'>
                      <StatusBadge
                        tone={item.kind === 'invitation' ? 'warning' : 'info'}
                        label={
                          item.kind === 'invitation'
                            ? 'Awaiting response'
                            : 'Offer in draft'
                        }
                        size='sm'
                        className='me-1'
                      />
                      {formatDateTime(item.deadline)}
                    </p>
                  </div>
                  <div className='text-end flex-shrink-0'>
                    <span
                      className={`fw-medium ${info.tone === 'muted' ? 'text-muted' : `text-${info.tone}`}`}
                    >
                      {info.label}
                    </span>
                    {allowed && (
                      <div>
                        <Link to={item.link} className='fs-13'>
                          {item.kind === 'invitation' ? 'Respond' : 'Continue'}
                        </Link>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};

UpcomingDeadlines.propTypes = { items: PropTypes.array.isRequired };

const PHASES = [
  {
    key: 'opening',
    label: 'Opening',
    color: 'primary',
    hint: 'Open for submission',
  },
  {
    key: 'pastDeadline',
    label: 'Past deadline',
    color: 'warning',
    hint: 'Waiting for the evaluation',
  },
  { key: 'closing', label: 'Closing', color: 'secondary', hint: 'Finished' },
];

const PhaseSummary = ({ data }) => {
  const total = PHASES.reduce((sum, p) => sum + (data[p.key] || 0), 0);

  return (
    <Card className='h-100 mb-0'>
      <CardHeader className='bg-transparent'>
        <h5 className='card-title mb-0'>Where your tenders stand</h5>
        <p className='text-muted fs-13 mb-0'>All invitations by phase.</p>
      </CardHeader>
      <CardBody>
        {total === 0 ? (
          <p className='text-muted mb-0' role='status'>
            No tender activity yet. Invitations will appear here once they are
            sent to you.
          </p>
        ) : (
          <>
            <div
              className='progress progress-lg mb-3'
              role='img'
              aria-label='Invitations by phase'
            >
              {PHASES.map(p => (
                <div
                  key={p.key}
                  className={`progress-bar bg-${p.color}`}
                  style={{ width: `${percentOf(data[p.key], total)}%` }}
                />
              ))}
            </div>
            <ul className='list-unstyled mb-0'>
              {PHASES.map(p => (
                <li
                  key={p.key}
                  className='d-flex align-items-center gap-2 mb-2'
                >
                  <span className={`badge bg-${p.color} p-1 rounded-circle`}>
                    <span className='visually-hidden'>{p.label}</span>
                  </span>
                  <span className='flex-grow-1'>
                    {p.label}
                    <span className='text-muted fs-12 ms-2'>{p.hint}</span>
                  </span>
                  <span className='fw-semibold'>{data[p.key] || 0}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardBody>
    </Card>
  );
};

PhaseSummary.propTypes = { data: PropTypes.object.isRequired };

const MitraDashboard = ({ token, user }) => {
  const { data, loading, error, updatedAt, reload } = useDashboardData(
    'mitra-overview',
    token,
    loadLegacyMitraOverview
  );

  const header = (
    <DashboardHeader
      user={user}
      subtitle='Your invitations, offers and deadlines at a glance.'
      updatedAt={updatedAt}
      onRefresh={reload}
      loading={loading}
    >
      <QuickActions actions={QUICK_ACTIONS} />
    </DashboardHeader>
  );

  if (error && !data) {
    return (
      <>
        {header}
        <DashboardError message={error} onRetry={reload} />
      </>
    );
  }

  if (!data) {
    return (
      <>
        {header}
        <KpiSkeleton />
      </>
    );
  }

  return (
    <>
      {header}

      {data.limited && (
        <LimitedNotice sections='pending invitations, draft offers, upcoming deadlines and your activity' />
      )}

      <Row className='g-3 mb-3'>
        <Col xl={3} sm={6}>
          <KpiCard
            label='Awaiting your response'
            value={data.awaitingResponse}
            icon='ri-mail-unread-line'
            color='warning'
            emphasis={data.awaitingResponse > 0}
            hint='Invitations not accepted or declined yet'
            to='/apps-invitation/view'
          />
        </Col>
        <Col xl={3} sm={6}>
          <KpiCard
            label='Offers in draft'
            value={data.draftOffers}
            icon='ri-draft-line'
            color='info'
            hint={`${data.deadlineSoon} due within 3 days`}
            to='/apps-offer/view'
          />
        </Col>
        <Col xl={3} sm={6}>
          <KpiCard
            label='Offers submitted'
            value={data.submitted}
            icon='ri-send-plane-line'
            color='success'
            hint={`of ${data.total} invitations`}
            to='/apps-offer/view'
          />
        </Col>
        <Col xl={3} sm={6}>
          <KpiCard
            label='Late or missed'
            value={data.missed}
            icon='ri-error-warning-line'
            color='danger'
            emphasis={data.missed > 0}
            hint={data.missed > 0 ? 'Review these offers' : 'No late offers'}
            to='/apps-offer/view'
          />
        </Col>
      </Row>

      {data.limited ? (
        <Row className='g-3'>
          <Col xl={7}>
            <AlertPanel alerts={data.alerts || []} />
          </Col>
          <Col xl={5}>
            <PhaseSummary data={data} />
          </Col>
        </Row>
      ) : (
        <>
          <Row className='g-3 mb-3'>
            <Col xl={7}>
              <AlertPanel alerts={data.alerts || []} />
            </Col>
            <Col xl={5}>
              <UpcomingDeadlines items={data.upcomingDeadlines || []} />
            </Col>
          </Row>

          <Row className='g-3'>
            <Col xl={7}>
              <PhaseSummary data={data} />
            </Col>
            <Col xl={5}>
              <ActivityFeed items={data.recentActivities || []} />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

MitraDashboard.propTypes = {
  token: PropTypes.string,
  user: PropTypes.object,
};

export default MitraDashboard;
